import Swiper from "./swiper-bundle.esm.browser.min.js";

const BREAKPOINT_TABLET = 600;
const BREAKPOINT_DESKTOP = 900;

class CustomTimelineSwiperSlider extends HTMLElement {
  constructor() {
    super();
    this._swiper = null;
    this._initialized = false;
    this._handleSectionLoad = this._handleSectionLoad.bind(this);
    this._handleSectionUnload = this._handleSectionUnload.bind(this);
    this._handleReadMoreChange = this._handleReadMoreChange.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    document.addEventListener("shopify:section:load", this._handleSectionLoad);
    document.addEventListener("shopify:section:unload", this._handleSectionUnload);
    this.addEventListener("read-more:change", this._handleReadMoreChange);
    this._initSwiper();
  }

  disconnectedCallback() {
    this._initialized = false;
    this._destroySwiper();
    this.removeEventListener("read-more:change", this._handleReadMoreChange);
    document.removeEventListener("shopify:section:load", this._handleSectionLoad);
    document.removeEventListener("shopify:section:unload", this._handleSectionUnload);
  }

  _handleReadMoreChange() {
    requestAnimationFrame(() => this._swiper?.update());
  }

  _isOurSection(event) {
    const section = this.closest(".shopify-section");
    return section && (event.target === section || section.contains(event.target));
  }

  _handleSectionLoad(event) {
    if (!this._isOurSection(event)) return;
    this._destroySwiper();
    this._initSwiper();
  }

  _handleSectionUnload(event) {
    if (!this._isOurSection(event)) return;
    this._destroySwiper();
  }

  _readConfig() {
    const slideCount = parseInt(this.dataset.slideCount || "0", 10);
    return {
      slideCount,
      spaceBetween: parseInt(this.dataset.spaceBetween || "12", 10),
      mobile: parseFloat(this.dataset.slidesPerViewMobile || "1.1"),
      tablet: parseFloat(this.dataset.slidesPerViewTablet || "2"),
      desktop: parseFloat(this.dataset.slidesPerViewDesktop || "3.5"),
      showArrows: this.dataset.showArrows === "true" && slideCount > 1,
      showPagination: this.dataset.showPagination === "true" && slideCount > 1,
      enableLoop: this.dataset.enableLoop === "true" && slideCount > 1,
      enableAutoplay: this.dataset.enableAutoplay === "true" && slideCount > 1,
      autoplaySpeed: parseInt(this.dataset.autoplaySpeed || "5000", 10),
      pauseOnHover: this.dataset.pauseOnHover === "true",
      pauseOnInteraction: this.dataset.pauseOnInteraction !== "false",
      sectionId: this.dataset.sectionId || "",
    };
  }

  _initSwiper() {
    const swiperEl = this.querySelector("[data-swiper]");
    const container = this.querySelector("[data-swiper-container]");
    const slideEls = this.querySelectorAll("[data-swiper-slide]");

    if (!swiperEl || !container || slideEls.length === 0) return;

    this._destroySwiper();

    swiperEl.classList.add("swiper", "timeline-swiper__swiper");
    container.classList.add("swiper-wrapper");
    slideEls.forEach((slide) => slide.classList.add("swiper-slide"));

    const config = this._readConfig();
    const swiperConfig = {
      grabCursor: true,
      spaceBetween: config.spaceBetween,
      slidesPerView: config.mobile,
      watchOverflow: true,
      speed: 300,
      breakpoints: {
        [BREAKPOINT_TABLET]: { slidesPerView: config.tablet },
        [BREAKPOINT_DESKTOP]: { slidesPerView: config.desktop },
      },
    };

    if (config.enableLoop) {
      swiperConfig.loop = true;
    }

    if (config.showArrows) {
      const nextEl = this.querySelector(
        `.timeline-swiper__nav-next--${config.sectionId}`,
      );
      const prevEl = this.querySelector(
        `.timeline-swiper__nav-prev--${config.sectionId}`,
      );
      if (nextEl && prevEl) {
        swiperConfig.navigation = { nextEl, prevEl };
      }
    }

    if (config.showPagination) {
      const paginationEl = this.querySelector(
        `.timeline-swiper__pagination--${config.sectionId}`,
      );
      if (paginationEl) {
        swiperConfig.pagination = {
          el: paginationEl,
          clickable: true,
          renderBullet: (index, className) => {
            const num = index + 1;
            const total = config.slideCount;
            return `<button type="button" class="${className}" aria-label="Slide ${num} of ${total}"></button>`;
          },
        };
      }
    }

    if (config.enableAutoplay) {
      swiperConfig.autoplay = {
        delay: config.autoplaySpeed,
        disableOnInteraction: config.pauseOnInteraction,
        pauseOnMouseEnter: config.pauseOnHover,
      };
    }

    this._swiper = new Swiper(swiperEl, swiperConfig);
    requestAnimationFrame(() => this._swiper?.update());
  }

  _destroySwiper() {
    if (this._swiper && !this._swiper.destroyed) {
      this._swiper.destroy(true, true);
    }
    this._swiper = null;
  }
}

if (!customElements.get("custom-timeline-swiper-slider")) {
  customElements.define("custom-timeline-swiper-slider", CustomTimelineSwiperSlider);
}
